import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { EmptyState } from '~/components/empty-state'
import { GetTheApps } from '~/components/get-the-apps'
import { DialogLink } from '~/components/dialog-link'
import { BarbellIcon, ChevronRightIcon, PlayIcon } from '~/components/icons'
import { KindMark } from '~/components/kind-mark'
import { SafeImage } from '~/components/safe-image'
import { SetGrid } from '~/components/set-grid'
import { SetDeck } from '~/components/set-deck'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { TimelineDayPill } from '~/components/timeline-day-pill'
import { WorkoutThumbnail } from '~/components/workout-thumbnail'
import { useApi, useDataService, useDataState } from '~/data/data-context'
import type { LoggedSet, TimelineDay, Workout } from '~/data/types'
import { dayKey, dayStart, formatDayLabel, formatMonthYear } from '~/lib/dates'
import { DAY_HEADING_HEIGHT, RUN_SPACING, SECTION_SPACING } from '~/lib/rhythm'
import type { DialogName } from '~/lib/dialogs'
import { effectiveRecentExercises, type RowExercise } from '~/lib/exercise-insights'
import { groupCountShown, setIsShown } from '~/lib/set-filters'
import { rowPlan, tilesHeight } from '~/lib/set-rows'
import { groupsOf, numbersInRun, timelineOrder } from '~/lib/set-runs'
import {
  armPageTransition,
  takePageReturnScroll,
  useTimelineTransitionName,
} from '~/lib/view-transitions'
import { takeReturnScroll } from '~/lib/opening-set'
import { isActive, type Enrollment } from '~/data/templates'
import { WeighInPhotosBlock } from '~/components/weighin-day'
import { DaySummaryTrigger } from '~/components/day-summary'
import { Dialog } from '~/components/dialog'
import { useWeighIns } from '~/data/weighins-store'
import { weighInSaysSomething, photoName, type WeighIn, type WeighInPhoto } from '~/data/weighins'
import { weighInPhotosHeight } from '~/lib/weighin-tiles'

/**
 * The active filters ride in the URL, so leaving for a set and coming back keeps them —
 * and the device remembers the last ones chosen, so the timeline opens the way it was left.
 */
interface TimelineSearch {
  readonly filmed?: boolean
  readonly warmups?: boolean
}

export const Route = createFileRoute('/')({
  validateSearch: (search): TimelineSearch => ({
    ...(search.filmed === true ? { filmed: true } : {}),
    ...(search.warmups === false ? { warmups: false } : {}),
  }),
  component: Timeline,
})

/*
 * The timeline is laid out from the index — a count of sets per day for the whole history —
 * rather than from the sets it happens to have. Knowing every day's height in advance is
 * what makes the page its true length from the first paint, so the scrubber spans the whole
 * history and pressing a date is an ordinary scroll to a known offset. Sets are fetched for
 * the days about to come into view, and only those days are mounted.
 */

/*
 * The vertical rhythm lives in `~/lib/rhythm`, so the timeline spaces itself by the same
 * ladder every other page does. Headings hug what they head — they sit low in their slot, so
 * the slack above them reads as separation from what came before.
 */
const GROUP_SPACING = RUN_SPACING
const DAY_SPACING = SECTION_SPACING
/**
 * How far down the window the day pill reaches: the header, and the pill floating under it. A
 * day whose heading has come up to this line is the day the pill names.
 */
const DAY_PILL_LINE = 124

/** Rendered beyond the viewport, so scrolling meets ready rows rather than empty space. */
const OVERSCAN = 1200

/** Columns per width, matching the grid's breakpoints. */
function columnsFor(width: number): number {
  if (width >= 1024) return 6
  if (width >= 768) return 5
  if (width >= 640) return 4
  return 3
}

/**
 * One day of the timeline: one block of tiles, however many workouts it took. A day is not
 * split by the workouts its sets belong to — see `/specs/app.spec.md`, Main page — so the
 * only thing standing under the date is the photos' heading, and then the sets themselves.
 */
interface DayBox extends TimelineDay {
  /** How the day's tiles are dealt into rows. */
  readonly plan: number[]
  /** How many of its sets the gallery keeps — the tiles the day actually shows. */
  readonly shown: number
  /** Distance from the top of the timeline to the top of this day. */
  readonly offset: number
  readonly height: number
  /** The day's weigh-in, whose photos stand over the day's sets. */
  readonly weighIn: WeighIn | null
}

/**
 * The index the timeline actually draws: the server's counts, raised wherever this device
 * holds more sets for a day than the server has counted — a set logged offline has to take
 * up room too.
 */
function effectiveIndex(
  serverIndex: readonly TimelineDay[],
  setsByDay: ReadonlyMap<string, LoggedSet[]>,
  workouts: readonly Workout[],
): TimelineDay[] {
  const byDay = new Map<string, TimelineDay>()
  for (const day of serverIndex) {
    byDay.set(day.day, day)
  }
  for (const [day, sets] of setsByDay) {
    const known = byDay.get(day)
    /*
     * A day whose sets are all here is described by them, not by the index: deleting a
     * workout leaves the server's grouping naming a workout that no longer exists, and
     * logging one offline leaves it out of a grouping made before it happened.
     */
    if (known === undefined || sets.length >= known.count) {
      byDay.set(day, {
        day,
        count: sets.length,
        groups: groupsOf(
          [...sets].reverse(),
          (id) => workouts.find((workout) => workout.id === id)?.name ?? null,
        ),
      })
    }
  }
  return [...byDay.values()].sort((a, b) => (a.day < b.day ? 1 : -1))
}

function groupSetsByDay(sets: readonly LoggedSet[]): Map<string, LoggedSet[]> {
  const byDay = new Map<string, LoggedSet[]>()
  for (const set of sets) {
    const day = dayKey(set.performedAt)
    const existing = byDay.get(day)
    if (existing) {
      existing.push(set)
    } else {
      byDay.set(day, [set])
    }
  }
  return byDay
}

/**
 * Every day's height and offset, and the total the page has to be. The filters shape the
 * layout here, from the index's counts alone: a day sizes itself by the sets it will show,
 * and a day left with nothing disappears with them.
 */
function layOutDays(
  days: readonly TimelineDay[],
  columns: number,
  width: number,
  weighInByDay: ReadonlyMap<string, WeighIn>,
): DayBox[] {
  const laid: DayBox[] = []
  for (const day of days) {
    // Every set the day kept, whichever workout it belonged to: the day is one block.
    const shown = day.groups.reduce((kept, group) => kept + groupCountShown(group), 0)
    const plan = rowPlan(shown, columns)
    /*
     * The weigh-in belongs to the day: its photos stand over the day's sets, and what it
     * measured is behind the date. A day of nothing but photos is still a day — the date and
     * the photos are the whole of it — but a day left with no sets by the filters and nothing
     * to look at instead goes with them, weigh-in and all: a lone date over an empty gallery
     * is a heading about a weighing.
     */
    const weighIn = weighInByDay.get(day.day) ?? null
    const photosHeight = weighIn === null ? 0 : weighInPhotosHeight(weighIn, width, columns)
    if (shown === 0 && photosHeight === 0) {
      laid.push({
        ...day,
        plan,
        shown,
        weighIn,
        offset: laid.reduce((down, box) => down + box.height, 0),
        height: 0,
      })
      continue
    }
    /*
     * What stands under the date: the photos, if any were taken, and then the day's sets.
     * They are the same kind of thing to the day — something that happened — so one gap
     * stands between them.
     */
    const parts = [photosHeight, shown === 0 ? 0 : tilesHeight(width, plan)].filter(
      (part) => part > 0,
    )
    const height =
      DAY_HEADING_HEIGHT +
      parts.reduce((tall, part) => tall + part, 0) +
      GROUP_SPACING * Math.max(0, parts.length - 1) +
      DAY_SPACING
    laid.push({
      ...day,
      plan,
      shown,
      weighIn,
      offset: laid.reduce((down, box) => down + box.height, 0),
      height,
    })
  }
  return laid
}

function Timeline() {
  const state = useDataState()
  const service = useDataService()
  const api = useApi()
  const [showingPhoto, setShowingPhoto] = useState<WeighInPhoto | null>(null)

  const setsByDay = useMemo(() => groupSetsByDay(state.sets), [state.sets])
  // The timeline's own list, laid out as the page lays it out and narrowed as the page is —
  // what a set opened from it is leafed through.
  const shownSets = useMemo(
    () => timelineOrder(state.sets).filter((set) => setIsShown(set)),
    [state.sets],
  )
  const setDays = useMemo(
    () => effectiveIndex(state.timelineIndex, setsByDay, state.workouts),
    [state.timelineIndex, setsByDay, state.workouts],
  )

  // The body's record joins the training's: a day appears when either has something on it.
  const weighIns = useWeighIns()
  const weighInByDay = useMemo(() => {
    const map = new Map<string, WeighIn>()
    for (const weighIn of weighIns ?? []) {
      if (weighInSaysSomething(weighIn)) {
        map.set(weighIn.day, weighIn)
      }
    }
    return map
  }, [weighIns])
  const days = useMemo(() => {
    /*
     * A day of its own is earned by something there is to look at: sets, or progress photos.
     * A weight and a tape measure are read on the heading of a day that is already there and
     * in the weigh-in's own pages — a day whose whole content is two numbers is a row you
     * scroll past, and a year of them is a timeline about weighing rather than about training.
     */
    const known = new Set(setDays.map((day) => day.day))
    const alone: TimelineDay[] = [...weighInByDay.entries()]
      .filter(([day, weighIn]) => !known.has(day) && weighIn.photos.length > 0)
      .map(([day]) => ({ day, count: 0, groups: [] }))
    return alone.length === 0
      ? setDays
      : [...setDays, ...alone].sort((a, b) => (a.day < b.day ? 1 : -1))
  }, [setDays, weighInByDay])

  // Width is measured rather than assumed: the column count and the tile size both follow
  // from it, and a day's height follows from those.
  const [width, setWidth] = useState(0)
  const [top, setTop] = useState(0)
  // Whether the days have been measured at all, which is what a scroll to one of them waits on.
  const [measured, setMeasured] = useState(false)
  const measure = useCallback((element: HTMLDivElement | null) => {
    if (element) {
      setWidth(element.clientWidth)
      setTop(element.getBoundingClientRect().top + window.scrollY)
      setMeasured(true)
    }
  }, [])

  const [scrollY, setScrollY] = useState(0)
  const [viewport, setViewport] = useState(0)

  /*
   * Returning from a set, a workout or an exercise opened from here: the page is put back
   * where it stood when the thing was opened — synchronously, in a layout effect, which runs
   * inside the router's view transition callback and therefore before the browser captures
   * the new state. The router restores the same scroll again a beat later, but by then the
   * picture has been taken: a day the capture found unmounted is a tile nothing could fly
   * back to, and a face photographed at the wrong scroll is a face the flight misses.
   */
  useLayoutEffect(() => {
    const returning = takeReturnScroll() ?? takePageReturnScroll()
    if (returning !== null && returning > 0) {
      window.scrollTo({ top: returning, left: 0, behavior: 'instant' })
      // The cascade is the point: the day has to be mounted before this commit is over,
      // because the browser photographs the page the moment the commit ends.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScrollY(returning)
    }
  }, [])
  // The rail shows itself while the timeline is moving and fades once it settles.
  const [railActive, setRailActive] = useState(false)
  const revealRail = useCallback(() => {
    setRailActive(true)
  }, [])

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY)
      setRailActive(true)
    }
    const onResize = () => {
      setViewport(window.innerHeight)
      setScrollY(window.scrollY)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // Hidden again once things have been still for a moment — set from the timer, not from
  // the body, so showing the rail never cascades a render.
  useEffect(() => {
    if (!railActive) {
      return
    }
    const timer = setTimeout(() => {
      setRailActive(false)
    }, 1500)
    return () => {
      clearTimeout(timer)
    }
  }, [railActive, scrollY])

  const columns = columnsFor(width)
  const boxes = useMemo(
    () => layOutDays(days, columns, width, weighInByDay),
    [days, columns, width, weighInByDay],
  )
  const total = boxes.reduce((sum, box) => sum + box.height, 0)

  const height = viewport === 0 ? window.innerHeight : viewport
  const from = scrollY - top - OVERSCAN
  const until = scrollY - top + height + OVERSCAN
  const shown = boxes.filter(
    (box) => box.height > 0 && box.offset + box.height >= from && box.offset <= until,
  )

  // Ask for the sets of the days about to be shown, in as few runs as they fall into.
  const wanted = shown
    .filter((box) => (setsByDay.get(box.day)?.length ?? 0) < box.count)
    .map((box) => box.day)
    .join(',')

  useEffect(() => {
    if (wanted === '') {
      return
    }
    const missing = new Set(wanted.split(','))
    let run: TimelineDay[] = []
    const runs: TimelineDay[][] = []
    for (const box of boxes) {
      if (missing.has(box.day)) {
        run.push({ day: box.day, count: box.count, groups: box.groups })
      } else if (run.length > 0) {
        runs.push(run)
        run = []
      }
    }
    if (run.length > 0) {
      runs.push(run)
    }
    for (const window of runs) {
      void service.loadDays(window)
    }
    // `boxes` is deliberately absent: the runs are decided by which days are wanted, and
    // re-running on every relayout would ask again for windows already in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, service])

  /*
   * The days the timeline heads, newest first — the only days the pill's calendar offers,
   * since a day with nothing on it here is nowhere the timeline could be brought to.
   */
  const laidDays = useMemo(() => boxes.filter((box) => box.height > 0), [boxes])
  const trainedSet = useMemo(() => new Set(laidDays.map((box) => box.day)), [laidDays])
  const firstDay = laidDays[0]
  // The line the pill hangs to, in the page's own offsets from the top of the days.
  const line = scrollY + DAY_PILL_LINE - top
  const onTheDays = measured && firstDay !== undefined && line >= firstDay.offset
  const topDay = onTheDays
    ? (laidDays.find((box) => box.offset + box.height > line) ?? laidDays.at(-1))?.day
    : undefined

  /** Brings a day's heading up to the pill. */
  const bringTo = useCallback(
    (day: string) => {
      const box = laidDays.find((each) => each.day === day)
      if (box !== undefined) {
        window.scrollTo({ top: top + box.offset - DAY_PILL_LINE + 1, left: 0, behavior: 'instant' })
      }
    },
    [laidDays, top],
  )

  const rowExercises = useMemo(
    () => effectiveRecentExercises(state.recentExercises, state.sets),
    [state.recentExercises, state.sets],
  )

  /*
   * Signed in, and this browser has never read the account: what it holds is nothing yet, not
   * an empty account. Neither "no workouts yet" nor a skeleton that will never fill is an
   * honest answer, so it says which it is.
   */
  if (state.couldNotBeRead) {
    return (
      <div className="py-16 text-center text-ink-muted">
        <p className="text-lg text-ink">Your workouts haven’t arrived yet</p>
        <p className="mt-2 text-sm">
          This browser hasn’t been able to reach your account. They will be here once it can.
        </p>
      </div>
    )
  }

  if (!state.timelineSettled || weighIns === null) {
    return (
      <SkeletonBlock label="Loading timeline" className="space-y-8 py-4">
        <Skeleton className="h-44 w-full rounded-2xl" />
        <Skeleton className="h-36 w-full rounded-2xl" />
        <div className="grid grid-cols-3 gap-1" aria-hidden>
          <Skeleton className="aspect-square" />
          <Skeleton className="aspect-square" />
          <Skeleton className="aspect-square" />
        </div>
      </SkeletonBlock>
    )
  }

  return (
    // What the timeline is showing, which is what a set opened from it is leafed through.
    <SetDeck sets={shownSets}>
      <div className="relative">
        <RecentWorkouts workouts={state.workouts} />
        <RecentExercises exercises={rowExercises} />
        {/*
        The third section of the timeline, headed like the two above it. The days follow at a
        heading's gap rather than a section's — the section they belong to has already been
        opened.
      */}
        {days.length > 0 && (
          <section aria-labelledby="timeline-sets" className="mb-4 space-y-4">
            <h2
              id="timeline-sets"
              className="px-0.5 text-xs font-medium tracking-wider text-ink-muted uppercase"
            >
              Timeline
            </h2>
          </section>
        )}

        {days.length === 0 ? (
          <BeginHere enrollments={state.enrollments} />
        ) : (
          <div ref={measure} className="relative" style={{ height: total }}>
            {shown.map((box) => (
              <DaySection
                key={box.day}
                box={box}
                sets={setsByDay.get(box.day) ?? []}
                columns={columns}
                onShowPhoto={setShowingPhoto}
              />
            ))}
          </div>
        )}

        {/* A progress photo, as big as the window gives it. */}
        {showingPhoto !== null && (
          <Dialog
            label={photoName(showingPhoto)}
            onDismiss={() => {
              setShowingPhoto(null)
            }}
            className="max-w-4xl bg-black p-2"
          >
            <SafeImage
              src={api.weighInPhotoUrl(showingPhoto.id)}
              alt={photoName(showingPhoto)}
              // The frame takes the rounding and the black: left bare it is a square grey box
              // behind a rounded photo, and `object-contain` letterboxes onto grey.
              frameClassName="rounded-2xl bg-black"
              className="max-h-[85dvh] w-full rounded-2xl object-contain"
            />
          </Dialog>
        )}

        {/*
          The day pill, over the days and not over what stands above them — `/specs/app.spec.md`,
          The day pill. It names whatever day stands at the top, however the page came to be
          there, and moves no day anywhere else: the food log keeps a day of its own.
        */}
        {firstDay !== undefined && (
          <TimelineDayPill
            // Above the days it is up behind the header, still naming the first of them so it
            // comes down already saying the day it will stand on.
            day={topDay ?? firstDay.day}
            shown={topDay !== undefined}
            pickable={(day) => trainedSet.has(day)}
            onPick={bringTo}
          />
        )}

        {days.length > 0 && total > 0 && (
          <Scrubber
            boxes={boxes.filter((box) => box.height > 0)}
            total={total}
            top={top}
            scrollY={scrollY}
            viewport={height}
            active={railActive}
            onReveal={revealRail}
          />
        )}
      </div>
    </SetDeck>
  )
}

/**
 * One day of the timeline, parked at its own offset: its date, then the things that happened
 * that day — the photos, and the day's sets as one block.
 */
function DaySection({
  box,
  sets,
  columns,
  onShowPhoto,
}: {
  box: DayBox
  sets: LoggedSet[]
  columns: number
  onShowPhoto: (photo: WeighInPhoto) => void
}) {
  // Days read newest first, and each day reads in the order it happened.
  const ascending = [...sets].reverse()
  /*
   * Numbered among the sets of the day that share a workout — or that share having none —
   * rather than over the day as a whole, so a set wears the number the tracker gave it however
   * many workouts the day took and whatever the filters leave.
   */
  const numbers = new Map<string, number>()
  for (const workoutId of new Set(ascending.map((set) => set.workoutId))) {
    const alike = ascending.filter((set) => set.workoutId === workoutId)
    numbersInRun(alike).forEach((number, at) => {
      const id = alike[at]?.id
      if (id !== undefined) {
        numbers.set(id, number)
      }
    })
  }
  const kept = ascending.filter((set) => setIsShown(set))

  return (
    <section
      id={`day-${box.day}`}
      // The day answers to its date, so what stands under the date can be found by the day
      // it belongs to rather than by the line it used to ride.
      aria-labelledby={`day-${box.day}-date`}
      className="absolute inset-x-0 flex flex-col"
      style={{ top: box.offset, height: box.height }}
    >
      {/* The date is the gallery's anchor, and the whole of the day's heading: everything
          that happened that day is named under it, and everything the day came to is behind
          it — see `/specs/features/day-summary.spec.md`. */}
      <h2
        id={`day-${box.day}-date`}
        className="flex items-end px-0.5 pb-4 text-[26px] leading-8 font-medium"
        style={{ height: DAY_HEADING_HEIGHT }}
      >
        <DaySummaryTrigger
          day={box.day}
          className="-mx-2 rounded-xl px-2 py-0.5 text-left transition-colors hover:bg-hover"
        >
          {formatDayLabel(dayStart(box.day))}
        </DaySummaryTrigger>
      </h2>
      <div className="flex flex-col" style={{ gap: GROUP_SPACING }}>
        {/* The day's photos lead the day, before any of its sets. */}
        {box.weighIn != null && (
          <WeighInPhotosBlock weighIn={box.weighIn} columns={columns} onShow={onShowPhoto} />
        )}
        {/*
          One block for the whole day. Nothing inside it heads an exercise or a workout: every
          tile names its own exercise, and a day that went back to one was being carved into a
          heading over nearly every tile. Numbered before the warm-ups go, so a set wears the
          number the tracker gave it and hiding the warm-ups renumbers nothing.
        */}
        {box.shown > 0 && (
          <SetGrid
            sets={kept}
            numbers={kept.map((set) => numbers.get(set.id) ?? undefined)}
            columns={columns}
            slots={box.shown}
          />
        )}
      </div>
    </section>
  )
}

/**
 * How many each row carries before the rest goes behind its way through. A row is for the few
 * you are moving between at the moment; past that it stops being a row you scan and becomes a
 * list you scroll, and a list belongs on a page of its own with a way to search it.
 */
const WORKOUTS_SHOWN = 8
const EXERCISES_SHOWN = 12

/** A section's name, and the way to the rest of it where there is a rest. */
function SectionHeading({
  id,
  title,
  seeAll,
}: {
  id: string
  title: string
  /** The page the rest of it is on, which opens over this one. */
  seeAll?: DialogName
}) {
  return (
    <div className="flex items-center justify-between px-0.5">
      {/* A quiet label, not a name: the sections recede so the days can be the loud ones. */}
      <h2 id={id} className="text-xs font-medium tracking-wider text-ink-muted uppercase">
        {title}
      </h2>
      {seeAll !== undefined && (
        <DialogLink
          dialog={seeAll}
          className="group/all flex items-center gap-1 rounded-full py-1 pl-3 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          See all
          <ChevronRightIcon className="h-5 w-5 transition-transform duration-150 group-hover/all:translate-x-1" />
        </DialogLink>
      )}
    </div>
  )
}

/**
 * The empty timeline teaches the ways in, shaped by where the account already is: a run
 * underway has a workout waiting, and an account with nothing yet is offered every door.
 */
function BeginHere({ enrollments }: { enrollments: Enrollment[] }) {
  const running = enrollments.find(isActive)
  if (running !== undefined) {
    return (
      <EmptyState
        icon={<PlayIcon className="h-8 w-8" />}
        title="Your plan is ready"
        detail={`Nothing logged yet — ${running.templateName} has your next workout waiting. Grab your phone, and every set lands here.`}
        actions={<GetTheApps compact />}
      />
    )
  }
  return (
    <EmptyState
      icon={<BarbellIcon className="h-8 w-8" />}
      title="No sets yet"
      detail="Log a workout and every set lands here, kept the way photos are. Ways to begin:"
      actions={
        <>
          <DialogLink
            dialog="explore"
            className="rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-on-accent"
          >
            Explore templates
          </DialogLink>
          <DialogLink
            dialog="templates"
            className="rounded-full px-5 py-2.5 text-sm font-medium ring-1 ring-white/15 transition-colors hover:bg-hover"
          >
            Create a template
          </DialogLink>
          {/* Logging itself lives in the apps, so the third door is the way to them. */}
          <GetTheApps compact />
        </>
      }
    />
  )
}

function RecentWorkouts({ workouts }: { workouts: Workout[] }) {
  if (workouts.length === 0) {
    return null
  }
  return (
    <section aria-labelledby="workouts-row" className="mb-10 space-y-4">
      {/* The way through only appears once there is a rest to go through to. */}
      <SectionHeading
        id="workouts-row"
        title="Recent workouts"
        {...(workouts.length > WORKOUTS_SHOWN ? ({ seeAll: 'workouts' } as const) : {})}
      />
      <SidewaysRow className="gap-2 pb-1">
        {workouts.slice(0, WORKOUTS_SHOWN).map((workout) => (
          <WorkoutThumbnail key={workout.id} workout={workout} />
        ))}
      </SidewaysRow>
    </section>
  )
}

/**
 * The exercises row: every exercise that has been done, most recently done first, each a
 * round face — the latest set thumbnail, or the mark of its kind — with the name beneath.
 * Round on purpose: workouts are cards, exercises are faces, the way people are in Photos.
 */
function RecentExercises({ exercises }: { exercises: RowExercise[] }) {
  if (exercises.length === 0) {
    return null
  }
  return (
    <section aria-labelledby="exercises-row" className="mb-10 space-y-4">
      {/*
        Always offered, however few there are. Behind it is the only place an exercise can be
        looked up by name, and an exercise you cannot remember the look of is exactly the one
        you go looking for — so the way in cannot depend on the row having overflowed.
      */}
      <SectionHeading id="exercises-row" title="Exercises" seeAll="exercises" />
      <SidewaysRow className="gap-3 pb-1">
        {exercises.slice(0, EXERCISES_SHOWN).map((exercise) => (
          <ExerciseFace key={exercise.name} exercise={exercise} />
        ))}
      </SidewaysRow>
    </section>
  )
}

/** One face in the exercises row — a component of its own so the shared name is a hook away. */
function ExerciseFace({ exercise }: { exercise: RowExercise }) {
  const api = useApi()
  const sharedName = useTimelineTransitionName('exercise', exercise.name)
  return (
    <Link
      to="/exercises/$exerciseName"
      params={{ exerciseName: exercise.name }}
      viewTransition
      style={{ viewTransitionName: sharedName }}
      onClick={(event) => {
        armPageTransition(event.currentTarget, 'exercise', exercise.name)
      }}
      className="group flex w-25 shrink-0 flex-col items-center gap-1.5 rounded-3xl px-2 py-2 transition-colors hover:bg-hover"
    >
      <span className="block h-20 w-20 overflow-hidden rounded-full bg-surface-raised">
        {exercise.thumbnailSetId !== null ? (
          <SafeImage
            src={api.mediaUrl(exercise.thumbnailSetId, 'thumbnail')}
            alt=""
            frameClassName="h-full w-full"
            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.06]"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-ink-muted/70 transition-colors duration-300 group-hover:bg-hover-faint">
            <KindMark records={exercise.records} className="h-10 w-10" />
          </span>
        )}
      </span>
      <span className="line-clamp-2 max-w-full text-center text-sm leading-tight text-ink-muted group-hover:text-ink">
        {exercise.name}
      </span>
    </Link>
  )
}

interface MonthMark {
  readonly month: string
  readonly label: string
  readonly isYear: boolean
  /** Where along the rail this month sits — its first day's share of the whole timeline. */
  readonly fraction: number
}

/** Where the rail hangs: clear of the header above, and of the workout bar below. */
const RAIL_TOP = 80
const RAIL_BOTTOM = 24

/** How tall a cursor on the rail stands, which is what has to stay inside the track. */
const CAPSULE_HEIGHT = 32

/**
 * Where a cursor sits down the track, kept whole inside it. Placed by its middle, a cursor at
 * either end of the rail would hang half of itself off the end of the line it rides — so the
 * ends it travels between are half a cursor in from the ends of the track.
 */
function capsuleTop(fraction: number): string {
  const half = CAPSULE_HEIGHT / 2
  return `calc(${String(half)}px + ${String(fraction)} * (100% - ${String(CAPSULE_HEIGHT)}px))`
}

/** The room one label takes down the rail, plus the air that keeps two of them apart. */
const LABEL_ROOM = 26

/**
 * Which marks get to say their name. A month sits where its sets do, so a quiet month lands
 * on top of the one before it — labelling every nth mark is what put Jun through Aug. Instead
 * a label is only given where there is room for one: years first, since a year is the mark
 * worth keeping, and then the months that still fit between them.
 */
function labelledMarks(marks: readonly MonthMark[], railHeight: number): Set<string> {
  const gap = railHeight === 0 ? 0 : LABEL_ROOM / railHeight
  const taken: number[] = []
  const fits = (at: number) => taken.every((held) => Math.abs(held - at) >= gap)
  const labelled = new Set<string>()
  for (const pass of [true, false]) {
    for (const mark of marks) {
      if (mark.isYear === pass && fits(mark.fraction)) {
        labelled.add(mark.month)
        taken.push(mark.fraction)
      }
    }
  }
  return labelled
}

/** [span] is what the rail runs across, which is the days less the window it is read through. */
function monthMarks(boxes: readonly DayBox[], span: number): MonthMark[] {
  const marks: MonthMark[] = []
  let previousYear: string | null = null
  for (const box of boxes) {
    const month = box.day.slice(0, 7)
    if (marks[marks.length - 1]?.month === month) {
      continue
    }
    const year = box.day.slice(0, 4)
    const changedYear = previousYear !== null && previousYear !== year
    marks.push({
      month,
      label: changedYear
        ? year
        : new Date(dayStart(box.day)).toLocaleDateString(undefined, { month: 'short' }),
      isYear: changedYear,
      fraction: span <= 0 ? 0 : Math.min(1, Math.max(0, box.offset / span)),
    })
    previousYear = year
  }
  return marks
}

/**
 * The Google Photos rail. Because the page is already its full length, a position on the
 * rail is a scroll offset and nothing else: dragging scrolls, scrolling moves the handle,
 * and a date years back is the same gesture as a date just above — no loading to wait for
 * before it can move, and no rail that grows as sets arrive.
 */
function Scrubber({
  boxes,
  total,
  top,
  scrollY,
  viewport,
  active,
  onReveal,
}: {
  boxes: readonly DayBox[]
  total: number
  top: number
  scrollY: number
  viewport: number
  active: boolean
  onReveal: () => void
}) {
  /*
   * What the rail runs between: the first heading over the days at the top of the window, and
   * the last one in the same place. Both ends are a heading come to rest where a heading comes
   * to rest, so every day between them can be reached — and above the first heading is the top
   * of the page rather than part of the history, which the rail spends none of itself on.
   *
   * Where the last day is short enough that its heading cannot reach the top, the rail ends
   * where the page does instead.
   */
  const lastHeading = boxes[boxes.length - 1]?.offset ?? 0
  const span = Math.max(1, Math.min(lastHeading, total - viewport))
  const marks = useMemo(() => monthMarks(boxes, span), [boxes, span])
  const railHeight = Math.max(0, viewport - RAIL_TOP - RAIL_BOTTOM)
  const labelled = useMemo(() => labelledMarks(marks, railHeight), [marks, railHeight])

  const fraction = Math.min(1, Math.max(0, (scrollY - top) / span))

  /** The day a fraction of the way along the rail would put at the top of the window. */
  const dayAt = (at: number) =>
    [...boxes].reverse().find((box) => box.offset <= at * span) ?? boxes[0]

  const scrollToFraction = (next: number) => {
    window.scrollTo({ top: top + next * span })
    onReveal()
  }

  // Where the pointer is over the rail, as its share of the rail's length.
  const [hover, setHover] = useState<number | null>(null)
  // While the rail is being dragged the page follows the pointer, so the two cursors say the
  // same thing. Only one of them is shown then: the one that is actually where you are.
  const [dragging, setDragging] = useState(false)

  // What the rail is pointing at: the drag's own position, or the pointer's resting over it.
  const pointing = dragging ? fraction : hover
  const pointedDay = pointing === null ? undefined : dayAt(pointing)?.day
  /** The day the page itself is on, which the rail says quietly beside its capsule. */
  const hereDay = dayAt(fraction)?.day

  return (
    <aside
      /*
       * Hard against the right edge, where the scrollbar used to be — the rail is the app's
       * scrollbar now, so nothing else rides beside it.
       *
       * Everything is laid out from the track, which stands 48px in from that edge. The
       * months are written beyond it, in the strip between the track and the window, where
       * nothing of the page reaches; the dates the rail says read back the other way, out
       * over the page. Nothing crosses the track, so a month and a date are never read on
       * top of each other however far the pointer travels.
       *
       * It takes no presses itself. What can be taken hold of is the band the track, the
       * cursors and the months stand in, below — and everything to the near side of that is
       * the rail being read, so a press there reaches whatever the page has drawn under it:
       * on a window narrow enough for the page to run beneath the rail, the last card of a
       * row or the edge of the last tile in a line.
       */
      className={`pointer-events-none fixed right-0 z-20 w-24 transition-opacity duration-300 ${
        active ? 'opacity-100' : 'opacity-0 hover:opacity-100'
      }`}
      style={{ top: RAIL_TOP, bottom: RAIL_BOTTOM }}
    >
      {marks.map((mark) => {
        return (
          <span
            key={mark.month}
            data-testid="rail-mark"
            className="absolute left-14 -translate-y-1/2"
            style={{ top: `${String(mark.fraction * 100)}%` }}
          >
            {labelled.has(mark.month) ? (
              <span
                className={`text-xs ${mark.isYear ? 'font-medium text-ink' : 'text-ink-muted'}`}
              >
                {mark.label}
              </span>
            ) : (
              <span className="block h-1 w-1 rounded-full bg-ink-muted/50" />
            )}
          </span>
        )
      })}

      {/*
        The rail's own track: a hairline for the two cursors to ride, with eight pixels of air
        between it and the months on one side and the dates on the other. Without it a cursor
        floats in the margin with nothing to be on, and the pair read as marks rather than as
        a place along a length.
      */}
      <span
        data-testid="rail-track"
        className="pointer-events-none absolute inset-y-0 left-12 w-px -translate-x-1/2 rounded-full bg-white/10"
      />

      {/* Where you are: an accent capsule on the track. */}
      <span
        data-testid="rail-cursor"
        className="pointer-events-none absolute left-12 h-8 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent"
        style={{ top: capsuleTop(fraction) }}
      />
      {/* Where the pointer rests: the same capsule faded, where a press would put you. It is
          only ever the resting pointer's — a drag has the bright one under the finger. */}
      {!dragging && hover !== null && (
        <span
          className="pointer-events-none absolute left-12 h-8 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/40"
          style={{ top: capsuleTop(hover) }}
        />
      )}
      {/*
        Where the pointer is pointing: a hairline drawn across the rail, the width of the
        labels it passes, so it reads as a level rather than as a second handle. It is only
        ever the resting pointer's — a drag moves the page, and the accent capsule under the
        finger is already saying where that has got to.
      */}
      {/*
        The two dates the rail can say, both as bare text reading back over the page from the
        near side of the track — nothing but a shadow to hold them against whatever they pass
        over, so the rail stays a rail rather than growing a panel every time the pointer
        moves. Where the page is sits quietly beside its capsule and steps aside the moment
        the rail is taken hold of; what the pointer is pointing at is the bright one.
      */}
      {!dragging && hereDay !== undefined && (
        <span
          className="pointer-events-none absolute right-14 -translate-y-1/2 rounded-full bg-surface/90 px-2 py-0.5 text-sm whitespace-nowrap text-ink-muted"
          style={{ top: `${String(fraction * 100)}%` }}
        >
          {formatDayLabel(dayStart(hereDay))}
        </span>
      )}
      {pointing !== null && pointedDay !== undefined && (
        <span
          className="rail-pill pointer-events-none absolute right-14 -translate-y-1/2 rounded-full bg-surface-overlay px-2 py-0.5 text-sm font-medium whitespace-nowrap text-ink shadow-lg shadow-black/40"
          style={{ top: `${String(pointing * 100)}%` }}
        >
          {formatDayLabel(dayStart(pointedDay))}
        </span>
      )}

      <input
        type="range"
        aria-label="Timeline scrubber"
        aria-orientation="vertical"
        aria-valuetext={formatMonthYear(dayStart((dayAt(fraction) ?? boxes[0])?.day ?? ''))}
        min={0}
        max={1000}
        step={1}
        value={Math.round(fraction * 1000)}
        onChange={(event) => {
          scrollToFraction(Number(event.target.value) / 1000)
        }}
        onPointerMove={(event) => {
          const rail = event.currentTarget.getBoundingClientRect()
          const at = rail.height === 0 ? 0 : (event.clientY - rail.top) / rail.height
          setHover(Math.min(1, Math.max(0, at)))
          onReveal()
        }}
        onPointerDown={() => {
          setDragging(true)
        }}
        onPointerUp={() => {
          setDragging(false)
        }}
        onPointerCancel={() => {
          setDragging(false)
        }}
        onPointerLeave={() => {
          setHover(null)
        }}
        /*
         * The rail's whole grab, hugging the window's edge: the months, the track, the
         * capsules on it, and enough room around them to aim at. Everything to the left of
         * this band is the rail being read rather than held, and presses pass through it to
         * the page. Resting here is also what wakes the rail, since hovering this wakes the
         * aside above it.
         *
         * Placed by the physical edges, not the logical ones. A vertical range is written
         * vertically, and on an element written that way `inset-block` is left and right —
         * so the logical spelling of "top and bottom" pins this to the wrong pair of edges
         * and lands the band on the far side of the rail from the one it is drawn on.
         */
        className="pointer-events-auto absolute top-0 right-0 h-full w-14 cursor-pointer opacity-0"
        style={{ writingMode: 'vertical-lr' }}
      />
    </aside>
  )
}
